import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ContactDetail, CrmChat } from '../components/crm';
import { Contact } from '../types/contact';
import { contactApi } from '../services/contactApi';
import { ContactListNavigationState } from '../types/contactNavigation';
import styles from './CrmPageLayout.module.css';

const ContactDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [contact, setContact] = React.useState<Contact | null>(null);
  const [loading, setLoading] = React.useState(true);
  const contactListState = (location.state as { contactListState?: ContactListNavigationState } | null)?.contactListState ?? null;

  const navigateToContacts = React.useCallback((replace: boolean = false) => {
    if (contactListState) {
      navigate('/contacts', {
        replace,
        state: { contactListState },
      });
      return;
    }

    navigate('/contacts', { replace });
  }, [contactListState, navigate]);

  const fetchContact = React.useCallback(async () => {
    try {
      if (id) {
        const data = await contactApi.get(id);
        setContact(data);
      }
    } catch (error) {
      console.error('Error fetching contact:', error);
      navigateToContacts(true);
    } finally {
      setLoading(false);
    }
  }, [id, navigateToContacts]);

  React.useEffect(() => {
    fetchContact();
  }, [fetchContact]);

  if (loading) {
    return <div className="loading">Loading contact...</div>;
  }

  if (!contact) {
    return null;
  }

  return (
    <div className={styles.pageLayout}>
      <div className={styles.contentColumn}>
        <ContactDetail
          contact={contact}
          onEdit={() => navigate(`/contacts/${contact.id}/edit`, {
            state: contactListState ? { contactListState } : undefined,
          })}
          onDelete={async () => {
            try {
              await contactApi.delete(contact.id);
              navigateToContacts(true);
            } catch (error) {
              console.error('Error deleting contact:', error);
            }
          }}
          onBack={() => navigateToContacts()}
          onContactUpdated={(updatedContact) => setContact(updatedContact)}
        />
      </div>
      <CrmChat
        focusContactId={contact.id}
        focusContactName={`${contact.firstName} ${contact.lastName}`}
        onDataChanged={fetchContact}
      />
    </div>
  );
};

export default ContactDetailPage;
